import { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { StanButton } from '@/components';
import { createIconElement } from '@/lib/utils/createIconElement';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { formatTimeTo12Hour } from '@/lib/utils/timeUtils';
import IcClose from '@/assets/icons/ic-x-close.svg';
import type { Appointment } from '../types/appointment.types';
import { APPOINTMENT_TYPES } from '../constants/appointmentData';

const CloseIcon = createIconElement(IcClose, { className: 'size-4' });
interface DayPopupProps {
    isOpen: boolean;
    onClose: () => void;
    date: Date;
    appointments: Appointment[];
    onOpenBlockTimeModal: () => void;
    onAppointmentClick?: (appointment: Appointment) => void;
    onBlockedTimeClick?: (appointment: Appointment) => void;
    position?: { x: number; y: number };
}

// Helper function to check if a block is an all-day block
const isAllDayBlock = (startTime: string, endTime: string): boolean => {
    // All-day blocks typically have startTime of 00:00 and endTime of 23:59 or 00:00 (next day)
    return startTime === '00:00' && (endTime === '23:59' || endTime === '00:00');
};

export function DayPopup({
    isOpen,
    onClose,
    date,
    appointments,
    onOpenBlockTimeModal,
    onAppointmentClick,
    onBlockedTimeClick,
    position,
}: DayPopupProps) {
    const floatingRef = useRef<HTMLDivElement | null>(null);
    const [isPositioned, setIsPositioned] = useState(false);
    const isMobile = useIsMobile();

    // Lock background scroll on mobile only when popup opens
    const lockScroll = useEffectEvent(() => {
        const mainContainer = document.querySelector('.no-scrollbar.overflow-y-auto') as HTMLElement;

        document.body.style.overflow = 'hidden';
        if (mainContainer) {
            mainContainer.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.overflow = '';
            if (mainContainer) {
                mainContainer.style.overflow = '';
            }
        };
    });

    useEffect(() => {
        if (!isOpen || !isMobile) return;
        return lockScroll();
    }, [isOpen, isMobile]);

    const virtualElement = useMemo(
        () => ({
            getBoundingClientRect: () => ({
                width: 0,
                height: 0,
                x: position?.x || 0,
                y: position?.y || 0,
                top: position?.y || 0,
                left: position?.x || 0,
                right: position?.x || 0,
                bottom: position?.y || 0,
            }),
        }),
        [position]
    );

    const { refs, floatingStyles } = useFloating({
        open: isOpen,
        placement: 'bottom-start',
        middleware: [
            offset(8),
            flip({
                fallbackAxisSideDirection: 'end',
            }),
            shift({
                padding: 16,
                crossAxis: true, // Also constrain on cross axis
            }),
        ],
        whileElementsMounted: autoUpdate,
    });

    const handleSetReference = useEffectEvent(() => {
        refs.setReference(virtualElement);
    });

    useEffect(() => {
        if (!isOpen) {
            setIsPositioned(false);
            return;
        }

        setIsPositioned(false);
        const timer = setTimeout(() => setIsPositioned(true), 10);
        return () => clearTimeout(timer);
    }, [isOpen]);

    // Set virtual element as reference
    useEffect(() => {
        handleSetReference();
    }, [virtualElement]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (floatingRef.current && !floatingRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    const formatDate = (date: Date) => {
        const month = date.toLocaleDateString('en-US', { month: 'long' });
        const day = date.getDate();
        const dayName = date.toLocaleDateString('en-US', { weekday: isMobile ? 'short' : 'long' });

        return (
            <>
                {month} {day}
                <span className='text-gray ml-2'>{dayName}</span>
            </>
        );
    };

    // Sort all appointments by start time
    const sortedAppointments = useMemo(() => {
        return [...appointments].sort((a, b) => {
            // Compare start times
            return a.startTime.localeCompare(b.startTime);
        });
    }, [appointments]);

    const isPastDate = date < new Date(new Date().setHours(0, 0, 0, 0));

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop for mobile */}
            <div className='z-backdrop fixed inset-0 bg-black/30 sm:hidden' onClick={onClose} />

            <div
                ref={element => {
                    floatingRef.current = element;
                    refs.setFloating(element);
                }}
                style={{
                    ...floatingStyles,
                    opacity: isPositioned ? 1 : 0,
                    transition: 'opacity 150ms ease-in-out',
                    maxHeight: 'calc(100vh - 32px)', // Ensure it fits within viewport with padding
                    width: '90%',
                    maxWidth: '400px',
                    overflowY: 'auto', // Ensure scrolling is enabled
                }}
                className='bg-canvas shadow-nucleus-elevation-high z-overlay-content fixed rounded-lg p-4 sm:p-6'
            >
                {/* Header */}
                <div className='mb-4 flex items-center justify-between'>
                    <h5 className='text-dark text-body-xs m-0 font-semibold'>{formatDate(date)}</h5>
                    <div className='flex items-center gap-2'>
                        {!isPastDate && (
                            <StanButton
                                name='block-time'
                                variant='ghost'
                                onClick={onOpenBlockTimeModal}
                                color='primary'
                                size='xs'
                            >
                                + Block Time
                            </StanButton>
                        )}
                        <button
                            onClick={onClose}
                            className='bg-neutral-subtle hover:bg-neutral-hover flex size-6 cursor-pointer items-center justify-center rounded-full transition-colors'
                            aria-label='Close popup'
                        >
                            {CloseIcon}
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className='flex flex-col gap-1 pt-4 pl-4'>
                    {sortedAppointments.length > 0 ? (
                        <div className='flex flex-col gap-1'>
                            {sortedAppointments.map((appointment, index) => {
                                const isBlocked =
                                    appointment.type === APPOINTMENT_TYPES.BLOCKED ||
                                    appointment.type === APPOINTMENT_TYPES.GOOGLE_MEET;
                                const isGcalBlock = appointment.type === APPOINTMENT_TYPES.GOOGLE_MEET;
                                const isBooking = appointment.type === APPOINTMENT_TYPES.BOOKING;

                                // For privacy, hide actual titles of Google Calendar events
                                const displayTitle = isGcalBlock ? 'Gcal Block' : appointment.title;

                                const isManualBlock = appointment.type === APPOINTMENT_TYPES.BLOCKED;
                                const isClickable =
                                    (isBooking && onAppointmentClick) ||
                                    (isManualBlock && !isPastDate && onBlockedTimeClick);

                                return (
                                    <div
                                        key={appointment.id || index}
                                        className={`mb-4 flex items-start gap-2 ${
                                            isClickable
                                                ? 'hover:bg-neutral-subtle cursor-pointer rounded transition-colors'
                                                : ''
                                        }`}
                                        onClick={() => {
                                            if (isBooking && onAppointmentClick) {
                                                onAppointmentClick(appointment);
                                            } else if (isManualBlock && !isPastDate && onBlockedTimeClick) {
                                                onClose();
                                                onBlockedTimeClick(appointment);
                                            }
                                        }}
                                    >
                                        <div
                                            className='mt-1 h-2 w-2 flex-shrink-0 rounded-full'
                                            style={{
                                                backgroundColor: isBooking
                                                    ? 'var(--bg-color-primary-brand-main)'
                                                    : isGcalBlock
                                                      ? 'var(--bg-color-neutral-background-subtle)'
                                                      : 'var(--text-color-negative-fg-negative-strong)',
                                            }}
                                        />
                                        <span
                                            className='text-xs'
                                            style={{
                                                color: isBlocked
                                                    ? isGcalBlock
                                                        ? 'var(--text-color-neutral-fg-tertiary)'
                                                        : 'var(--text-color-negative-fg-negative-strong)'
                                                    : 'var(--text-color-primary)',
                                            }}
                                        >
                                            {isBlocked && isAllDayBlock(appointment.startTime, appointment.endTime) ? (
                                                `All Day Block`
                                            ) : (
                                                <>
                                                    {formatTimeTo12Hour(appointment.startTime)} -{' '}
                                                    {formatTimeTo12Hour(appointment.endTime)} {displayTitle}
                                                </>
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className='py-8 text-center'>
                            <p className='text-gray text-body-sm'>No appointments or blocked times for this day</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
